// src/page/InformationNew.js
import React, { useEffect, useRef, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function InformationNew() {
    useAuthGuard();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [publishStatus, setPublishStatus] = useState('0'); // '0' 非公開 / '1' 公開
    const [popupStatus, setPopupStatus] = useState('0');     // '0' OFF / '1' ON
    const [publicRange, setPublicRange] = useState('');
    const [saving, setSaving] = useState(false);

    const pad = (n) => String(n).padStart(2, '0');
    const fpRef = useRef(null);

    useEffect(() => {
        if (!window.flatpickr) return;
        fpRef.current = window.flatpickr('#public_range', {
            mode: 'range',
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onClose: (selectedDates) => {
                if (selectedDates.length === 2) {
                    const f = (d) =>
                        `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    const [s, e] = selectedDates;
                    setPublicRange(`${f(s)} ~ ${f(e)}`);
                }
            }
        });
        return () => { try { fpRef.current?.destroy(); } catch { } };
    }, []);

    const RANGE_RE = /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/;
    const parseLocal = (str) => {
        const [datePart, timePart] = str.trim().split(' ');
        const [yyyy, mm, dd] = datePart.split('/').map(Number);
        const [HH, ii] = timePart.split(':').map(Number);
        const d = new Date(yyyy, mm - 1, dd, HH, ii, 0, 0);
        return isNaN(d) ? null : d;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const errs = [];
        const t = title.trim();
        const c = content.trim();
        if (!t) errs.push('タイトルは必須です。');
        if (t.length > 20) errs.push('タイトルは20文字以内で入力してください。');
        if (!c) errs.push('内容は必須です。');
        if (c.length > 2048) errs.push('内容は2048文字以内で入力してください。');
        if (!publicRange) {
            errs.push('公開期間を入力してください。');
        } else if (!RANGE_RE.test(publicRange)) {
            errs.push('公開期間は YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm 形式で入力してください。');
        }

        let startISO = '', endISO = '';
        if (errs.length === 0) {
            const [lhs, rhs] = publicRange.split('~').map(s => s.trim());
            const d1 = parseLocal(lhs);
            const d2 = parseLocal(rhs);
            if (!d1 || !d2) errs.push('公開期間の日時が不正です。');
            else if (d1.getTime() > d2.getTime()) errs.push('公開開始は公開終了以前である必要があります。');
            else {
                startISO = d1.toISOString();
                endISO = d2.toISOString();
            }
        }
        if (errs.length) { alert(errs.join('\n')); return; }

        const payload = {
            informationTitle: t,
            informationDescription: c,
            publishedFlag: publishStatus === '1',
            popupFlg: popupStatus === '1',
            publishedStartDate: startISO,
            publishedEndDate: endISO
        };

        try {
            setSaving(true);
            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/information`, {
                method: 'POST',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const msg = `登録に失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); throw new Error(j.error || msg); }
                catch { throw new Error(msg); }
            }
            alert('登録しました。');
            window.history.back();
        } catch (e) {
            alert(e.message || '登録に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="/information">お知らせ管理</a></li>
                        <li className="breadcrumb-item active">新規登録</li>
                    </ol>
                </nav>

                {/* HTML structure preserved */}
                <form className="mb-9" onSubmit={handleSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto">
                            <h2 className="mb-2">お知らせ登録</h2>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button" onClick={() => window.history.back()} disabled={saving}>キャンセル</button>
                            <button className="btn btn-primary mb-2 mb-sm-0" type="submit" disabled={saving}>{saving ? '登録中...' : '登録'}</button>
                        </div>
                    </div>

                    <div className="row g-5">
                        <div className="col-12 col-xl-8">
                            <div className="mb-4">
                                <h4 className="mb-3">タイトル<span className="text-danger">*</span></h4>
                                <input className="form-control" type="text" name="voucher_name" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={20} />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">内容<span className="text-danger">*</span></h4>
                                <textarea className="form-control" name="content" rows="4" value={content} onChange={(e) => setContent(e.target.value)} required maxLength={2048}></textarea>
                            </div>
                        </div>

                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="0" checked={publishStatus === '0'} onChange={() => setPublishStatus('0')} required /> 非公開
                                                            </label>
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="1" checked={publishStatus === '1'} onChange={() => setPublishStatus('1')} /> 公開
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">ポップアップフラグ<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input type="radio" name="popup_status" value="0" checked={popupStatus === '0'} onChange={() => setPopupStatus('0')} required /> OFF
                                                            </label>
                                                            <label className="mb-0">
                                                                <input type="radio" name="popup_status" value="1" checked={popupStatus === '1'} onChange={() => setPopupStatus('1')} /> ON
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <input className="form-control" type="text" name="public_range" id="public_range"
                                                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                            value={publicRange} onChange={(e) => setPublicRange(e.target.value)}
                                                            required
                                                            pattern="^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$"
                                                            title="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm の形式で入力してください" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </form>
            </>
        </Layout>
    );
}
